<template>
    <div class="key-class-analysis">
        <!-- 配置面板 -->
        <el-card class="config-card">
            <template #header>
                <span>重点班流动分析配置</span>
            </template>

            <el-form :model="config" label-width="100px">
                <el-form-item label="目标年级" required>
                    <el-select v-model="config.grade" placeholder="选择年级" style="width: 120px">
                        <el-option label="高一" value="高一" />
                        <el-option label="高二" value="高二" />
                        <el-option label="高三" value="高三" />
                    </el-select>
                </el-form-item>

                <el-divider content-position="left">考试配置</el-divider>

                <div v-for="(exam, examIndex) in config.exams" :key="examIndex" class="exam-config-block">
                    <div class="exam-header">
                        <span class="exam-title">考试 {{ examIndex + 1 }}</span>
                        <el-button type="danger" text @click="removeExam(examIndex)" v-if="config.exams.length > 1">
                            删除
                        </el-button>
                    </div>

                    <el-row :gutter="16">
                        <el-col :span="8">
                            <el-form-item label="学期">
                                <el-select v-model="exam.semester_id" placeholder="选择学期" style="width: 100%">
                                    <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :span="8">
                            <el-form-item label="考试类型">
                                <el-select v-model="exam.exam_type" placeholder="选择或输入考试" style="width: 100%"
                                    allow-create filterable>
                                    <el-option-group label="常规考试">
                                        <el-option label="月考1" value="monthly_1" />
                                        <el-option label="期中考试" value="midterm" />
                                        <el-option label="月考2" value="monthly_2" />
                                        <el-option label="期末考试" value="final" />
                                    </el-option-group>
                                    <el-option-group label="高三模拟">
                                        <el-option label="一模" value="mock_1" />
                                        <el-option label="二模" value="mock_2" />
                                        <el-option label="三模" value="mock_3" />
                                    </el-option-group>
                                    <el-option-group label="其他">
                                        <el-option label="月考3" value="monthly_3" />
                                        <el-option label="诊断性考试" value="diagnostic" />
                                        <el-option label="联合模拟" value="joint_mock" />
                                    </el-option-group>
                                </el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :span="8">
                            <el-form-item label="权重">
                                <el-input-number v-model="exam.weight" :min="0" :max="1" :step="0.1" :precision="2"
                                    style="width: 100%" />
                            </el-form-item>
                        </el-col>
                    </el-row>

                    <div class="subject-config-row">
                        <div v-for="subRow in exam.subjects" :key="subRow.subject" class="subject-item">
                            <span class="sub-name">{{ subRow.subject }}</span>
                            <el-input-number v-model="subRow.target_scale" :min="1" :max="200" size="small"
                                class="sub-input" />
                            <el-switch v-model="subRow.is_half_weight" size="small" title="是否折半" class="sub-switch" />
                        </div>
                    </div>
                </div>

                <el-button type="primary" plain @click="addExam" class="add-exam-btn">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    添加考试
                </el-button>

                <el-divider />

                <el-form-item>
                    <el-button type="primary" @click="calculate" :loading="calculating">
                        <el-icon>
                            <DataAnalysis />
                        </el-icon>
                        开始计算
                    </el-button>
                    <el-button @click="exportResult" :disabled="!hasResults">
                        <el-icon>
                            <Download />
                        </el-icon>
                        导出结果
                    </el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <!-- 结果展示 -->
        <el-card class="result-card" v-if="hasResults">
            <template #header>
                <div class="card-header">
                    <span>计算结果 (共 {{ results.total_count }} 名学生)</span>
                </div>
            </template>

            <el-table :data="results.results" stripe max-height="600" :header-cell-style="{ textAlign: 'center' }"
                :cell-style="{ textAlign: 'center' }">
                <el-table-column prop="rank" label="排名" width="70" fixed align="center" />
                <el-table-column prop="student_no" label="学号" width="120" align="center" />
                <el-table-column prop="name" label="姓名" width="100" align="center" />
                <el-table-column prop="class_name" label="班级" width="100" align="center" />

                <!-- 动态生成考试成绩列 -->
                <el-table-column v-for="(exam, eIdx) in config.exams" :key="eIdx" :label="getExamLabel(exam)"
                    align="center">
                    <el-table-column v-for="subject in subjects" :key="`${eIdx}_${subject}`" :label="subject" width="70"
                        align="center">
                        <template #default="{ row }">
                            {{ getSubjectScore(row, exam, subject) }}
                        </template>
                    </el-table-column>
                    <el-table-column :label="'小计'" width="80" align="center">
                        <template #default="{ row }">
                            <strong>{{ getExamTotal(row, exam) }}</strong>
                        </template>
                    </el-table-column>
                </el-table-column>

                <el-table-column prop="final_score" label="最终分" width="100" fixed="right" align="center">
                    <template #default="{ row }">
                        <el-tag type="primary" effect="dark">{{ row.final_score }}</el-tag>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { Plus, DataAnalysis, Download } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { semesterApi, gradeApi } from '@/api'

const semesters = ref([])
const calculating = ref(false)
const results = ref({ results: [], total_count: 0 })
const subjects = ['语文', '数学', '英语', '物理', '化学']

const hasResults = computed(() => results.value.results.length > 0)

// 配置
const config = reactive({
    grade: '高一',
    exams: []
})

// 创建默认科目配置
const createDefaultSubjects = () => {
    return subjects.map(s => ({
        subject: s,
        target_scale: 120,
        is_half_weight: s === '物理' || s === '化学'
    }))
}

// 创建默认考试配置
const createDefaultExam = () => ({
    semester_id: null,
    exam_type: 'monthly_1',
    weight: 0.25,
    subjects: createDefaultSubjects()
})

// 添加考试
const addExam = () => {
    const exam = createDefaultExam()
    if (semesters.value.length > 0) {
        exam.semester_id = semesters.value[0].id
    }
    config.exams.push(exam)
}

// 删除考试
const removeExam = (index) => {
    config.exams.splice(index, 1)
}

// 获取考试标签
const getExamLabel = (exam) => {
    const semester = semesters.value.find(s => s.id === exam.semester_id)
    const examTypeMap = {
        'monthly_1': '月考1',
        'midterm': '期中考试',
        'monthly_2': '月考2',
        'monthly_3': '月考3',
        'final': '期末考试',
        'mock_1': '一模',
        'mock_2': '二模',
        'mock_3': '三模',
        'diagnostic': '诊断性考试',
        'joint_mock': '联合模拟'
    }
    const semesterName = semester ? semester.name.substring(0, 10) : ''
    return `${semesterName} ${examTypeMap[exam.exam_type] || exam.exam_type}`
}

// 获取科目分数
const getSubjectScore = (row, exam, subject) => {
    const examKey = `${exam.semester_id}_${exam.exam_type}`
    const examData = row.exam_scores?.[examKey]
    if (!examData) return '-'
    const subjectData = examData[subject]
    if (!subjectData) return '-'
    return subjectData.normalized ?? '-'
}

// 获取考试小计
const getExamTotal = (row, exam) => {
    const examKey = `${exam.semester_id}_${exam.exam_type}`
    const examData = row.exam_scores?.[examKey]
    if (!examData) return '-'
    return examData._total ?? '-'
}

// 计算
const calculate = async () => {
    // 验证
    if (!config.grade) {
        ElMessage.warning('请选择年级')
        return
    }
    if (config.exams.length === 0) {
        ElMessage.warning('请至少添加一个考试')
        return
    }

    const totalWeight = config.exams.reduce((sum, e) => sum + e.weight, 0)
    if (Math.abs(totalWeight - 1) > 0.01) {
        ElMessage.warning(`考试权重之和应为1，当前为 ${totalWeight.toFixed(2)}`)
        return
    }

    calculating.value = true
    try {
        const request = {
            grade: config.grade,
            exams: config.exams.map(e => ({
                semester_id: e.semester_id,
                exam_type: e.exam_type,
                weight: e.weight,
                subjects: e.subjects.map(s => ({
                    subject: s.subject,
                    target_scale: s.target_scale,
                    is_half_weight: s.is_half_weight
                }))
            }))
        }
        const data = await gradeApi.calculateKeyClass(request)
        if (data.results && data.results.length > 0) {
            // 前端再次确保按总分降序排列
            data.results.sort((a, b) => b.final_score - a.final_score)
            // 重新标记排名以确保正确显示
            data.results.forEach((r, idx) => r.rank = idx + 1)
        }
        results.value = data
        ElMessage.success(`计算完成，共 ${results.value.total_count} 名学生`)
    } catch (e) {
        console.error(e)
    } finally {
        calculating.value = false
    }
}

// 导出结果
const exportResult = async () => {
    try {
        const request = {
            grade: config.grade,
            exams: config.exams.map(e => ({
                semester_id: e.semester_id,
                exam_type: e.exam_type,
                weight: e.weight,
                subjects: e.subjects.map(s => ({
                    subject: s.subject,
                    target_scale: s.target_scale,
                    is_half_weight: s.is_half_weight
                }))
            }))
        }
        const blob = await gradeApi.exportKeyClass(request)

        // 下载文件
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `重点班分析_${config.grade}.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)

        ElMessage.success('导出成功')
    } catch (e) {
        console.error(e)
    }
}

// 初始化
const fetchSemesters = async () => {
    try {
        semesters.value = await semesterApi.getAll()
        // 添加默认考试配置
        if (config.exams.length === 0) {
            addExam()
        }
    } catch (e) {
        console.error(e)
    }
}

onMounted(fetchSemesters)
</script>

<style scoped>
.key-class-analysis {
    max-width: 1600px;
}

.config-card {
    margin-bottom: 16px;
}

.result-card {
    margin-top: 16px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.card-header span {
    font-size: 16px;
    font-weight: 500;
}

.exam-config-block {
    background: #f9fafb;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    border: 1px solid #e5e7eb;
}

.exam-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.exam-title {
    font-weight: 600;
    color: #4f46e5;
}

.subject-config-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 12px;
    padding: 12px;
    background: white;
    border-radius: 4px;
    border: 1px solid #f0f0f0;
}

.subject-item {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fcfcfc;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #f0f0f0;
}

.sub-name {
    font-size: 13px;
    font-weight: 500;
    color: #606266;
    min-width: 30px;
}

.sub-input {
    width: 90px !important;
}

.sub-switch {
    margin-left: 4px;
}

.add-exam-btn {
    margin-bottom: 16px;
}
</style>
